<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between align-items-center">
        <h6 class="element-header">
          Interessenten
        </h6>

        <Buttons/>
      </div>

      <div class="element-box">
        <ul class="nav nav-tabs mb-5">
          <li class="nav-item">
            <p class="nav-link cursor-pointer"
               @click="router.push({ name: 'unemployedNewAndActiveList' })"
               aria-current="page">Kunden</p>
          </li>
          <li class="nav-item">
            <p class="nav-link cursor-pointer active"
               @click="router.push({ name: 'unemployedFinishedList'})"
               aria-current="page">Interessenten</p>
          </li>
        </ul>

        <Search
            :filters="filters"
            :loading="isSearching"
            :unemployedDropdown="unemployedDropdown"
            :officeDropdown="officeDropdown"
            :status="status"
            @search="getPotentialUnemployedList"/>

        <div class="row align-items-center justify-content-between mt-3">
          <div class="col-auto">
            <PerPage
                :values="[10, 25, 50, 100]"
                @update:perPage="getPotentialUnemployedList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>

          <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
            <input name="searchTerm"
                   @input="handleDebounceSearch"
                   class="form-control"
                   v-model="filterDebounce.searchTerm"
                   :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>TYP</th>
              <th>BEREICH</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Standort</th>
              <th>Kontaktperson</th>
              <th>Telefonnummer</th>
              <th>Erstellungsdatum</th>
              <th style="text-align: right; padding-right: 30px; min-width: 150px;">
                {{ $t('general.actions') }}
              </th>
            </tr>

            </thead>
            <tbody>
            <tr v-for="(unemployed) in unemployedList.data" :key="unemployed.id">
              <td>{{ unemployed?.statusTranslated }}</td>
              <td>{{ unemployed?.interestedIn }}</td>
              <td>{{ unemployed?.firstName }}</td>
              <td>{{ unemployed?.lastName }}</td>
              <td>{{ unemployed?.location?.name }}</td>
              <td>{{ unemployed?.contactPerson?.fullName }}</td>
              <td>{{ unemployed?.phone }}</td>
              <td>{{ unemployed?.createdAt }}</td>

              <td class="text-end">
                <button class="btn btn-primary"
                        @click="router.push({ name: 'unemployedPotentialForm', params: { id: unemployed.id } })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ms-2" @click="deleteModal(unemployed?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <Pagination
            v-if="unemployedList?.pagination?.total_pages > 1"
            :totalPages="unemployedList?.pagination?.total_pages"
            :currentPage="unemployedList?.pagination?.current_page"
            :totalItems="unemployedList?.pagination?.total_items"
            :perPage="unemployedList?.pagination?.items_per_page"
            @pagechanged="getPotentialUnemployedList"/>
      </div>

      <ModalDelete
          v-model="showModalDelete"
          :header="t('general.delete_item_header')"
          :onDelete="deleteUnemployed"/>
    </div>
  </PageTemplate>
</template>


<script setup>
import unemployedService from '@/api/Unemployed';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {getOfficeDropdown, getPotentialUnemployedDropdown} from '@/shared/dropdowns';
import {downloadFile} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Buttons from '@/views/App/Unemployed/List/Buttons.vue';
import Search from '@/views/App/Unemployed/List/Potential/Search.vue';
import debounce from 'lodash/debounce';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const {t} = useI18n();
const {enums} = piniaEnumsStore();
const unemployedId = ref(null);
const unemployedList = ref([]);
const unemployedDropdown = ref([]);
const officeDropdown = ref([])
const showModalDelete = ref(false);
const isLoaded = ref(false);
const isExcelLoaded = ref(false);

const filters = ref({
  status: '',
  interestedIn: '',
  lastName: '',
  contactPersonId: '',
  locationId: ''
});

const status = computed(() =>
    enums.unemployedStatus.filter((status) =>
        status.value === 'potential' || status.value === 'waiting_list' || status.value === 'finished',
    ),
);

const isSearching = ref(false);

const filterDebounce = ref({
  searchTerm: '',
});

const debouncedSearch = debounce(async (query) => {
  getPotentialUnemployedList(1, undefined, filterDebounce.value);
}, 300);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

async function getPotentialUnemployedList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = unemployedList.value?.pagination?.items_per_page;

  try {
    const {data} = await unemployedService.getUnemployedPotential({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    unemployedList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function deleteModal(id) {
  unemployedId.value = null;
  CloseToast();
  showModalDelete.value = true;
  unemployedId.value = id;
}

async function deleteUnemployed() {
  try {
    filters.value = {
      status: '',
      interestedIn: '',
      lastName: '',
      contactPersonId: '',
    };

    showModalDelete.value = false;

    await unemployedService.deleteUnemployed(unemployedId.value);
    await getPotentialUnemployedList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getExcelExport() {
  await downloadFile('/unemployed/potential-export', undefined,
      {...filters.value, searchTerm: filterDebounce.value.searchTerm},
      false,
      'GET',
      isExcelLoaded
  )
}


onMounted(async () => {
  await Promise.all([
    getPotentialUnemployedList(),
    getPotentialUnemployedDropdown(unemployedDropdown, 'lastName', 'lastName'),
    getOfficeDropdown(officeDropdown),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>
.nav.sm {
  width: max-content;
}
</style>